import React, { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { cn, formatCurrency, formatHours } from '@/lib/utils'
import { motion } from 'framer-motion'
import dayjs from 'dayjs'
import type { ProjectSummary } from '@/lib/types'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface ProjectHealthDashboardProps {
  summary: ProjectSummary | undefined
  startDate: string | null | undefined
  dueDate: string | null | undefined
  dateDone: string | null | undefined
  financeAccess?: 'full' | 'mini' | 'none'
  hasVendorWork?: boolean
}

const ProgressBar = ({
  value,
  gradient,
  delay = 0,
}: {
  value: number
  gradient: string
  delay?: number
}) => (
  <div className='w-full h-2 rounded-full overflow-hidden bg-muted/50 border border-border/40 shadow-inner'>
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${Math.min(value, 100)}%` }}
      transition={{ duration: 0.9, ease: 'easeOut', delay }}
      className={`h-full rounded-full ${gradient}`}
    />
  </div>
)

/* ─────────────────── Financial breakdown bar ─────────────────── */
// Tooltips are Radix (hover-scoped to their own trigger + auto-positioned by
// the Popper), not manually-placed absolute elements — each one opens and
// closes independently, so hovering one segment never leaves a second,
// unrelated tooltip stuck on screen overlapping it.
const TooltipBody = ({
  label,
  value,
  detail,
  subDetail,
  accentClass = 'text-foreground',
}: {
  label: string
  value: string
  detail?: string | undefined
  subDetail?: string | undefined
  accentClass?: string
}) => (
  <div className='text-left min-w-[150px] space-y-1 py-0.5'>
    <p className={cn('text-xs font-bold', accentClass)}>{label}</p>
    <p className={cn('text-lg font-bold', accentClass)}>{value}</p>
    {detail && (
      <p className='text-[11px] text-popover-foreground/60'>({detail})</p>
    )}
    {subDetail && (
      <p className='text-[11px] text-popover-foreground/60'>({subDetail})</p>
    )}
  </div>
)

const BarSegment = ({
  widthPct,
  colorClass,
  label,
  value,
  pct,
  detail,
  dimmed = false,
}: {
  widthPct: number
  colorClass: string
  label: string
  value: string
  pct: number
  detail?: string | undefined
  dimmed?: boolean
}) =>
  widthPct > 0 ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${widthPct}%` }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
          className={cn(
            'h-full flex items-center justify-center px-2 text-white truncate cursor-default transition-opacity duration-200',
            dimmed && 'opacity-30',
            colorClass
          )}
        >
          {widthPct > 14 ? (
            <span className='flex items-baseline gap-1.5 truncate'>
              <span className='text-base sm:text-lg font-bold'>{value}</span>
              <span className='text-[11px] sm:text-xs font-semibold whitespace-nowrap'>
                {label} ({Math.round(pct)}%)
              </span>
            </span>
          ) : (
            <span className='text-[11px] sm:text-xs font-bold'>{value}</span>
          )}
        </motion.div>
      </TooltipTrigger>
      <TooltipContent side='top' sideOffset={12}>
        <TooltipBody
          label={label}
          value={value}
          detail={detail}
          accentClass={colorClass.replace('bg-', 'text-')}
        />
      </TooltipContent>
    </Tooltip>
  ) : null

const BracketLabel = ({
  label,
  offsetPct,
  widthPct,
  position,
  amount,
  detail,
  pct,
  accentClass = 'text-blue-600 dark:text-blue-400',
  dimmed = false,
  onHoverChange,
}: {
  label: string
  offsetPct: number
  widthPct: number
  position: 'top' | 'bottom'
  amount: string
  detail?: string | undefined
  pct?: number | undefined
  accentClass?: string
  dimmed?: boolean
  onHoverChange?: (hovering: boolean) => void
}) => {
  if (widthPct <= 0) return null
  return (
    <div
      className='relative h-9'
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
    >
      <div
        className={cn(
          'absolute h-2 border-muted-foreground/40 transition-opacity duration-200',
          dimmed && 'opacity-30',
          position === 'top'
            ? 'bottom-1.5 border-t border-l border-r'
            : 'top-1.5 border-b border-l border-r'
        )}
        style={{ left: `${offsetPct}%`, width: `${widthPct}%` }}
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'absolute -translate-x-1/2 flex items-baseline gap-1 text-muted-foreground whitespace-nowrap cursor-default hover:text-foreground transition-colors transition-opacity duration-200',
              dimmed && 'opacity-30',
              position === 'top' ? 'top-0' : 'bottom-0'
            )}
            style={{ left: `${offsetPct + widthPct / 2}%` }}
          >
            <span className='text-sm sm:text-base font-bold'>{amount}</span>
            <span className='text-[11px] font-semibold'>
              {label}
              {pct !== undefined && ` (${Math.round(pct)}%)`}
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent
          side={position === 'top' ? 'top' : 'bottom'}
          sideOffset={12}
        >
          <TooltipBody
            label={label}
            value={amount}
            detail={detail}
            subDetail={
              pct !== undefined ? `${Math.round(pct)}% of Revenue` : undefined
            }
            accentClass={accentClass}
          />
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

const ProjectHealthDashboard: React.FC<ProjectHealthDashboardProps> = ({
  summary,
  startDate,
  dueDate,
  dateDone,
  financeAccess = 'none',
  hasVendorWork = false,
}) => {
  const [hoveredBracket, setHoveredBracket] = useState<
    'gross' | 'loaded' | null
  >(null)

  if (!summary) return null

  const canSeeFinance = financeAccess === 'full' || financeAccess === 'mini'
  const isFullFinance = financeAccess === 'full'

  /* ── capacity / time metrics ── */
  const capacity = Number(summary.total_hours) || 0
  const billable = Number(summary.billable_duration_seconds || 0) / 3600
  const nonBillable = Number(summary.nonbillable_duration_seconds || 0) / 3600
  const totalTracked = billable + nonBillable
  const hoursLeft = capacity - totalTracked
  const isOverCapacity = hoursLeft < 0
  const capacityUsedPct =
    capacity > 0 ? Math.min(100, (totalTracked / capacity) * 100) : 0

  const start = startDate ? dayjs(startDate) : null
  const due = dueDate ? dayjs(dueDate) : null
  const done = dateDone ? dayjs(dateDone) : null
  const today = dayjs()
  const totalDays = start && due ? due.diff(start, 'day') : 0
  const daysLeft = due ? due.diff(today, 'day') : 0
  const daysSpent = start ? today.diff(start, 'day') : 0
  const isOverdue = !done && daysLeft < 0
  const isCompleted = !!done
  const timelinePct =
    totalDays > 0
      ? Math.min(100, Math.max(0, (daysSpent / totalDays) * 100))
      : 0

  const totalBudget = Number(summary.total_budget) || 0
  const billableRevenue = canSeeFinance
    ? Number(summary.project_billable_amount) || 0
    : 0
  const budgetPct =
    totalBudget > 0 ? Math.min(100, (billableRevenue / totalBudget) * 100) : 0
  const budgetRemaining = totalBudget - billableRevenue

  /* ── financial breakdown (full finance only) ── */
  const billableCost = Number(summary.billable_cost) || 0
  const nonBillableCost =
    Number(
      summary.nonbillable_cost ?? summary.probono_cost ?? summary.probon_cost
    ) || 0
  const directLabor =
    Number(summary.total_cost) || billableCost + nonBillableCost
  const overhead = Number(summary.overhead_cost) || 0
  const fullyLoaded =
    Number(summary.fully_loaded_cost) || directLabor + overhead
  // Already folded into directLabor/fullyLoaded server-side (mergeVendorCost) -
  // broken out here just as its own pill, same as SummaryTable's Vendor Cost row.
  const vendorCost = Number(summary.vendor_cost) || 0
  const netProfit = Number(summary.net_profit ?? billableRevenue - fullyLoaded)
  const isProfit = netProfit >= 0
  const marginPct =
    billableRevenue > 0 ? (netProfit / billableRevenue) * 100 : 0
  const grossProfit = billableRevenue - directLabor

  const employeeCostPct =
    billableRevenue > 0
      ? Math.min(100, Math.max(0, (directLabor / billableRevenue) * 100))
      : 0
  const overheadPct =
    billableRevenue > 0
      ? Math.min(
          100 - employeeCostPct,
          Math.max(0, (overhead / billableRevenue) * 100)
        )
      : 0
  const netProfitPct = Math.max(0, 100 - employeeCostPct - overheadPct)

  return (
    <TooltipProvider delayDuration={150}>
      <div className='mb-8 space-y-6 animate-in fade-in duration-500'>
        {/* ── Project Analysis ── */}
        <div className='rounded-2xl border border-border/60 bg-card shadow-sm p-6'>
          <h3 className='text-base font-bold text-foreground mb-4'>
            Project Analysis
          </h3>
          <div
            className={`grid grid-cols-1 gap-4 ${
              canSeeFinance ? 'md:grid-cols-3' : 'md:grid-cols-2'
            }`}
          >
            {/* Timeline */}
            <div className='rounded-xl border border-border/60 bg-muted/10 p-5 space-y-4'>
              <span className='text-sm text-muted-foreground font-medium'>
                Timeline
              </span>

              {isCompleted ? (
                <>
                  <div className='flex items-end justify-between gap-2'>
                    <span className='text-2xl font-bold tabular-nums text-emerald-500'>
                      Done
                    </span>
                  </div>
                  <ProgressBar
                    value={100}
                    gradient='bg-gradient-to-r from-emerald-400 to-emerald-600'
                  />
                  <div className='text-[11px] font-medium text-muted-foreground'>
                    Completed {done!.format('MMM D, YYYY')}
                  </div>
                </>
              ) : (
                <>
                  <div className='flex items-end justify-between gap-2'>
                    <div className='flex items-baseline gap-1'>
                      <span
                        className={`text-2xl font-bold tabular-nums ${isOverdue ? 'text-destructive' : 'text-foreground'}`}
                      >
                        {isOverdue
                          ? `${Math.abs(daysLeft)}d`
                          : `${Math.max(0, daysSpent)}d`}
                      </span>
                      <span className='text-sm font-medium text-muted-foreground'>
                        /{Math.max(0, totalDays)}d
                      </span>
                    </div>
                    <span className='text-xs font-semibold text-muted-foreground shrink-0'>
                      {Math.round(isOverdue ? 100 : timelinePct)}%{' '}
                      <span className='font-normal'>progress</span>
                    </span>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <ProgressBar
                          value={isOverdue ? 100 : timelinePct}
                          gradient={
                            isOverdue
                              ? 'bg-gradient-to-r from-destructive/70 to-destructive'
                              : 'bg-gradient-to-r from-blue-400 to-blue-600'
                          }
                          delay={0.1}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side='top' sideOffset={17}>
                      <p className='text-sm font-bold'>
                        {Math.round(isOverdue ? 100 : timelinePct)}% Timeline is
                        consumed
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <div className='flex items-center justify-between text-[11px] font-medium text-muted-foreground'>
                    <span>{start ? start.format('MMM D') : '—'}</span>
                    <span>{due ? due.format('MMM D') : '—'}</span>
                  </div>
                </>
              )}
            </div>

            {/* Billed Hours */}
            <div className='rounded-xl border border-border/60 bg-muted/10 p-5 space-y-4'>
              <span className='text-sm text-muted-foreground font-medium'>
                Billed Hours
              </span>
              <div className='flex items-end justify-between gap-2'>
                <div className='flex items-baseline gap-1'>
                  <span
                    className={`text-2xl font-bold tabular-nums ${isOverCapacity ? 'text-destructive' : 'text-foreground'}`}
                  >
                    {formatHours(billable)}h
                  </span>
                  <span className='text-sm font-medium text-muted-foreground'>
                    /{formatHours(capacity)}h
                  </span>
                </div>
                <span className='text-xs font-semibold text-muted-foreground shrink-0'>
                  {Math.round(capacityUsedPct)}%{' '}
                  <span className='font-normal'>progress</span>
                </span>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='w-full h-2 rounded-full overflow-hidden bg-muted/50 border border-border/40 shadow-inner flex'>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${capacity > 0 ? Math.min(100, (billable / capacity) * 100) : 0}%`,
                      }}
                      transition={{
                        duration: 0.9,
                        ease: 'easeOut',
                        delay: 0.15,
                      }}
                      className='h-full bg-gradient-to-r from-blue-400 to-blue-600'
                    />
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${capacity > 0 ? Math.min(100 - (billable / capacity) * 100, (nonBillable / capacity) * 100) : 0}%`,
                      }}
                      transition={{
                        duration: 0.9,
                        ease: 'easeOut',
                        delay: 0.3,
                      }}
                      className='h-full bg-gradient-to-r from-red-400 to-red-600'
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  side='top'
                  sideOffset={17}
                  className='px-3.5 py-3'
                >
                  <div className='min-w-[190px] space-y-2'>
                    <p className='text-base font-bold'>
                      {formatHours(totalTracked)}h Worked
                    </p>
                    <div className='border-t border-dashed border-popover-foreground/20' />
                    <div className='space-y-1.5'>
                      <p className='flex items-center gap-2 text-xs font-semibold'>
                        <span className='inline-block w-2 h-2 rounded-full bg-blue-500 shrink-0' />
                        {formatHours(billable)}h{' '}
                        <span className='font-normal text-popover-foreground/50'>
                          (
                          {totalTracked > 0
                            ? Math.round((billable / totalTracked) * 100)
                            : 0}
                          %)
                        </span>{' '}
                        Billed
                      </p>
                      <p className='flex items-center gap-2 text-xs font-semibold'>
                        <span className='inline-block w-2 h-2 rounded-full bg-red-500 shrink-0' />
                        {formatHours(nonBillable)}h{' '}
                        <span className='font-normal text-popover-foreground/50'>
                          (
                          {totalTracked > 0
                            ? Math.round((nonBillable / totalTracked) * 100)
                            : 0}
                          %)
                        </span>{' '}
                        Non-Billed
                      </p>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
              <div className='text-[11px] font-medium text-muted-foreground'>
                {isOverCapacity
                  ? `${formatHours(Math.abs(hoursLeft))}h over capacity`
                  : `${formatHours(hoursLeft)}h Remaining`}
              </div>
            </div>

            {/* Budget – only for finance / mini_finance */}
            {canSeeFinance && (
              <div className='rounded-xl border border-border/60 bg-muted/10 p-5 space-y-4'>
                <span className='text-sm text-muted-foreground font-medium'>
                  Budget
                </span>
                <div className='flex items-end justify-between gap-2'>
                  <div className='flex items-baseline gap-1'>
                    <span className='text-2xl font-bold tabular-nums text-foreground'>
                      {formatCurrency(billableRevenue)}
                    </span>
                    <span className='text-xs font-medium text-muted-foreground'>
                      Billed
                    </span>
                    <span className='text-sm font-medium text-muted-foreground'>
                      /{formatCurrency(totalBudget)}
                    </span>
                  </div>
                  <span className='text-xs font-semibold text-muted-foreground shrink-0'>
                    {Math.round(budgetPct)}%{' '}
                    <span className='font-normal'>progress</span>
                  </span>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <ProgressBar
                        value={budgetPct}
                        gradient='bg-gradient-to-r from-amber-400 to-amber-600'
                        delay={0.2}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side='top' sideOffset={17}>
                    <p className='text-sm font-bold'>
                      {Math.round(budgetPct)}% billed is consumed
                    </p>
                  </TooltipContent>
                </Tooltip>
                <div className='text-[11px] font-medium text-muted-foreground'>
                  {formatCurrency(Math.max(0, budgetRemaining))} Remaining
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Financial Summary ── */}
        {canSeeFinance && (
          <div className='rounded-2xl border border-border/60 bg-card shadow-sm p-6'>
            <h3 className='text-xl font-bold text-[#45505e]  text-foreground mb-4'>
              Financial Summary
            </h3>
            <div className='rounded-xl border border-border/60 p-5 sm:p-4 bg-[#45505e]/5'>
              {isFullFinance && billableRevenue > 0 ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className='flex items-center gap-2 pb-4 mb-4 border-b border-border/50 cursor-default'>
                      <span className='text-xl font-bold text-foreground text-[#45505e]'>
                        Total Revenue
                      </span>
                      <span className='text-2xl font-bold tabular-nums text-foreground text-[#45505e]'>
                        {formatCurrency(billableRevenue)}
                      </span>
                    </div>
                  </TooltipTrigger>
                  {/* <TooltipContent
                    side='bottom'
                    sideOffset={12}
                    className='px-4 py-3'
                  >
                    <div className='min-w-[230px] space-y-2'>
                      <div className='flex items-center justify-between gap-4'>
                        <span className='flex items-center gap-1.5'>
                          <span className='inline-block w-2 h-2 rounded-full bg-emerald-500 shrink-0' />
                          <span className='text-sm font-bold text-emerald-500'>
                            {formatCurrency(billableRevenue)}
                          </span>
                          <span className='text-xs text-popover-foreground/70'>
                            Revenue
                          </span>
                        </span>
                        <span className='text-xs font-semibold text-popover-foreground/70'>
                          [100%]
                        </span>
                      </div>

                      <div className='border-t border-dashed border-popover-foreground/20' />

                      <div className='space-y-1.5'>
                        <div className='flex items-center gap-1.5'>
                          <span className='inline-block w-2 h-2 rounded-full bg-red-500 shrink-0' />
                          <span className='text-xs font-bold text-red-500'>
                            Cost
                          </span>
                        </div>
                        <div className='flex items-center justify-between gap-4 pl-4'>
                          <span className='flex items-center gap-1.5'>
                            <span className='inline-block w-1.5 h-1.5 rounded-full bg-red-500/70 shrink-0' />
                            <span className='text-xs text-popover-foreground'>
                              Direct Employee Cost
                            </span>
                          </span>
                          <span className='text-xs font-semibold text-popover-foreground'>
                            {formatCurrency(directLabor)}{' '}
                            <span className='font-normal text-popover-foreground/50'>
                              ({Math.round(employeeCostPct)}%)
                            </span>
                          </span>
                        </div>
                        <div className='flex items-center justify-between gap-4 pl-4'>
                          <span className='flex items-center gap-1.5'>
                            <span className='inline-block w-1.5 h-1.5 rounded-full bg-red-500/70 shrink-0' />
                            <span className='text-xs text-popover-foreground'>
                              Overhead Cost
                            </span>
                          </span>
                          <span className='text-xs font-semibold text-popover-foreground'>
                            {formatCurrency(overhead)}{' '}
                            <span className='font-normal text-popover-foreground/50'>
                              ({Math.round(overheadPct)}%)
                            </span>
                          </span>
                        </div>
                      </div>

                      <div className='border-t border-dashed border-popover-foreground/20' />

                      <div className='flex items-center justify-between gap-4 pl-4'>
                        <span className='flex items-center gap-1.5'>
                          <span className='inline-block w-1.5 h-1.5 rounded-full bg-red-500/40 shrink-0' />
                          <span className='text-xs text-popover-foreground/70'>
                            Fully Loaded Cost
                          </span>
                        </span>
                        <span className='text-xs font-semibold text-popover-foreground/70'>
                          {formatCurrency(fullyLoaded)}{' '}
                          <span className='font-normal text-popover-foreground/50'>
                            ({Math.round(employeeCostPct + overheadPct)}%)
                          </span>
                        </span>
                      </div>

                      <div className='border-t border-popover-foreground/20' />

                      <div className='flex items-center justify-between gap-4'>
                        <span className='flex items-center gap-1.5'>
                          <span className='inline-block w-2 h-2 rounded-full bg-violet-500 shrink-0' />
                          <span className='text-xs font-bold text-violet-500'>
                            Gross Profit
                          </span>
                        </span>
                        <span className='text-xs font-semibold text-popover-foreground'>
                          {formatCurrency(grossProfit)}{' '}
                          <span className='font-normal text-popover-foreground/50'>
                            (
                            {Math.round(
                              billableRevenue > 0
                                ? (grossProfit / billableRevenue) * 100
                                : 0
                            )}
                            %)
                          </span>
                        </span>
                      </div>
                      <div className='flex items-center justify-between gap-4'>
                        <span className='flex items-center gap-1.5'>
                          <span
                            className={cn(
                              'inline-block w-2 h-2 rounded-full shrink-0',
                              isProfit ? 'bg-blue-500' : 'bg-destructive'
                            )}
                          />
                          <span
                            className={cn(
                              'text-xs font-bold',
                              isProfit ? 'text-blue-500' : 'text-destructive'
                            )}
                          >
                            Net Profit
                          </span>
                        </span>
                        <span className='text-xs font-semibold text-popover-foreground'>
                          {formatCurrency(netProfit)}{' '}
                          <span className='font-normal text-popover-foreground/50'>
                            ({Math.round(netProfitPct)}%)
                          </span>
                        </span>
                      </div>
                    </div>
                  </TooltipContent> */}
                </Tooltip>
              ) : (
                <div className='flex items-center justify-between pb-4 mb-4 border-b border-border/50'>
                  <span className='text-base font-bold text-foreground'>
                    Total Revenue
                  </span>
                  <span className='text-xl font-bold tabular-nums text-foreground'>
                    {formatCurrency(billableRevenue)}
                  </span>
                </div>
              )}

              <div className='flex flex-wrap items-center gap-3 mb-6'>
                {isFullFinance && (
                  <span className='inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20'>
                    <span className='text-2xl font-bold tabular-nums text-red-600'>
                      {formatCurrency(fullyLoaded)}
                    </span>
                    <span className='text-sm font-semibold text-foreground/80'>
                      Total Cost ({formatCurrency(directLabor)} EC,{' '}
                      {formatCurrency(overhead)} OC)
                    </span>
                  </span>
                )}
                {isFullFinance && (
                  <span className='inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20'>
                    <span className='text-2xl font-bold tabular-nums text-emerald-600'>
                      {formatCurrency(grossProfit)}
                    </span>
                    <span className='text-sm font-semibold text-foreground/80'>
                      Gross profit (
                      {Math.round(
                        billableRevenue > 0
                          ? (grossProfit / billableRevenue) * 100
                          : 0
                      )}
                      %)
                    </span>
                  </span>
                )}
                {isFullFinance && hasVendorWork && (
                  <span className='inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20'>
                    <span className='text-base font-bold tabular-nums text-orange-600'>
                      {formatCurrency(vendorCost)}
                    </span>
                    <span className='text-sm font-semibold text-foreground/80'>
                      Vendor Cost
                    </span>
                  </span>
                )}
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 px-4 py-2 rounded-full border',
                    isProfit
                      ? 'bg-emerald-500/10 border-emerald-500/20'
                      : 'bg-destructive/10 border-destructive/20'
                  )}
                >
                  <span
                    className={cn(
                      'text-2xl font-bold tabular-nums',
                      isProfit ? 'text-foreground' : 'text-destructive'
                    )}
                  >
                    {formatCurrency(netProfit)}
                  </span>
                  <span className='text-sm font-semibold text-foreground/80'>
                    NET Profit ({Math.round(marginPct)}%)
                  </span>
                </span>
              </div>

              {isFullFinance && billableRevenue > 0 && (
                <div className='pt-3'>
                  <BracketLabel
                    label='Gross Profit'
                    offsetPct={employeeCostPct}
                    widthPct={100 - employeeCostPct}
                    position='top'
                    amount={formatCurrency(grossProfit)}
                    detail='Revenue-Employee Cost'
                    pct={
                      billableRevenue > 0
                        ? (grossProfit / billableRevenue) * 100
                        : 0
                    }
                    dimmed={hoveredBracket === 'loaded'}
                    onHoverChange={hovering =>
                      setHoveredBracket(hovering ? 'gross' : null)
                    }
                  />
                  <div className='w-full h-10 rounded-lg overflow-hidden flex bg-muted/40'>
                    <BarSegment
                      widthPct={employeeCostPct}
                      colorClass='bg-red-500'
                      label='Employee Cost'
                      value={formatCurrency(directLabor)}
                      pct={employeeCostPct}
                      detail={`${Math.round(employeeCostPct)}% of Revenue`}
                      dimmed={hoveredBracket === 'gross'}
                    />
                    <BarSegment
                      widthPct={overheadPct}
                      colorClass='bg-amber-500'
                      label='Overhead Cost'
                      value={formatCurrency(overhead)}
                      pct={overheadPct}
                      detail={`${Math.round(overheadPct)}% of Revenue`}
                    />
                    <BarSegment
                      widthPct={netProfitPct}
                      colorClass={
                        isProfit ? 'bg-emerald-600' : 'bg-destructive'
                      }
                      label='Net Profit'
                      value={formatCurrency(netProfit)}
                      pct={netProfitPct}
                      detail={`${Math.round(netProfitPct)}% of Revenue`}
                      dimmed={hoveredBracket === 'loaded'}
                    />
                  </div>
                  <BracketLabel
                    label='Fully Loaded Cost'
                    offsetPct={0}
                    widthPct={employeeCostPct + overheadPct}
                    position='bottom'
                    amount={formatCurrency(fullyLoaded)}
                    detail='Employee Cost + Overhead'
                    pct={employeeCostPct + overheadPct}
                    accentClass='text-red-600'
                    dimmed={hoveredBracket === 'gross'}
                    onHoverChange={hovering =>
                      setHoveredBracket(hovering ? 'loaded' : null)
                    }
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Alert banner ── */}
        {nonBillable > billable && totalTracked > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className='flex items-center gap-3 px-5 py-3.5 rounded-xl border border-destructive/20 bg-destructive/5'
          >
            <div className='p-2 rounded-lg bg-destructive/10 shrink-0'>
              <AlertTriangle className='w-4 h-4 text-destructive' />
            </div>
            <p className='text-xs font-semibold text-destructive leading-relaxed'>
              Non-billable hours exceed billable output — review time
              allocation.
            </p>
          </motion.div>
        )}
      </div>
    </TooltipProvider>
  )
}

export default ProjectHealthDashboard
